// The close-in shot recipes, mockup round 048 (R1007).
//
// One entry per look id of `labels.toml shot_melee_styles`, keyed by the id.
// The skirmish, the brawler and the torpedo roles fire these (R1009).
// `combat-styles.ts` prefers this map over the AUTO look, so a look that is
// not here yet draws the AUTO look and the map fills in one card at a time.
//
// **PENDING names every card that is not built yet.** An id must stand in
// `RECIPES` or in `PENDING`, and `combat-recipes-melee.test.ts` fails when one
// stands in neither. An agent that builds a card moves its id out of `PENDING`,
// adds its row to `data/presentation/combat.toml [[combat.shot_melee]]`, and
// touches no other file.
//
// **A recipe reads its figures out of the row and holds no literal.** The row
// carries `flightSec`, `rounds`, `stepSec` and `jitterPx` — which the LAYER
// spends, not the recipe — plus the eleven figure fields the card's own TOML
// comment maps. A PROPORTION is not a figure: how a fade is shaped, how far a
// muzzle bloom floors, how wide an arc opens, stays here as a module constant,
// the way `RAY_INNER` does in the effect kit.
//
// This is a leaf (`leaves.test.ts`): it imports the kit and the generated
// pack, and never an engine.

import {
  COMBAT_WHITE, PALE_INK, arcOf, atOn, blob, dart, dirOf, disc, flare, hash,
  line, polyline, rect, trail,
  type Dir, type Pt, type ShotRecipeFn,
} from './combat-recipe-kit';

// ---------------------------------------------------------------------------
// The proportions
// ---------------------------------------------------------------------------

/** point-blank-flash, card 10: the white core's floor width, in world px.
 *  The card writes `2.2 * f + 0.4`, so the core never thins to nothing. */
const FLASH_CORE_FLOOR = 0.4;
/** point-blank-flash: the muzzle bloom's floor radius, the card's `+ 2`. */
const FLASH_MUZZLE_FLOOR = 2;
/** point-blank-flash: how much of the peak alpha the muzzle bloom takes. */
const FLASH_MUZZLE_ALPHA = 0.9;
/** point-blank-flash: the round head's alpha. It never fades: the head IS
 *  the round, and a head that faded would read as a miss. */
const FLASH_HEAD_ALPHA = 0.95;

/** stitch, card 01: how much of the flight the muzzle flare shows for, and
 *  how bright it opens. */
const STITCH_MUZZLE_AT = 0.2, STITCH_MUZZLE_ALPHA = 0.5;

/** burst-fire, card 02: the round head's alpha, which never fades. */
const BURST_HEAD_ALPHA = 0.9;
/** burst-fire: how much of the flight the muzzle cross shows for. */
const BURST_MUZZLE_AT = 0.25;
/** burst-fire: how much wider the cross opens over that window, as a share
 *  of its own half span, and how far the forward stroke leads the bar. */
const BURST_CROSS_GROW = 1, BURST_CROSS_LEAD = 1.5;

/** broadside, card 04: how far the outer beams spread past `width2Px`, as a
 *  share of it. The card writes `5 + 4 * hash`. */
const BROADSIDE_SPREAD = 0.8;
/** broadside: how far the round bows out of the straight line at mid
 *  flight, as a share of the stand-off it left the beam at. */
const BROADSIDE_BOW = 1.5;
/** broadside: how far ahead the round looks to find its own heading. */
const BROADSIDE_LOOKAHEAD = 0.02;
/** broadside: how much of the flight the beam flares show for. */
const BROADSIDE_MUZZLE_AT = 0.25;
/** broadside: the round head's alpha. */
const BROADSIDE_HEAD_ALPHA = 0.95;

/** spread-cone, card 05: the smallest grain of the cone and how much bigger
 *  the largest one is, as shares of the row's figures. */
const CONE_SIZE_MIN = 0.7, CONE_SIZE_SPAN = 0.6;

/** spear-and-case, card 09: the white tip's alpha. */
const CASE_TIP_ALPHA = 0.95;
/** spear-and-case: how much faster the case runs than the dart, so it is
 *  gone before the dart lands. */
const CASE_RUSH = 1.2;
/** spear-and-case: how far off the muzzle the case is thrown, as a share of
 *  how far astern it starts. */
const CASE_SIDE = 0.5;
/** spear-and-case: how thin the case is against its own length. */
const CASE_THIN = 0.5;

/** lunge, card 12: which half of the flight the ghost bow slides over. */
const LUNGE_HALF = 0.5;
/** lunge: how much of the ghost's alpha stands while it is home, so the bow
 *  never disappears altogether. */
const LUNGE_REST = 0.3;
/** lunge: how far ahead of the ghost's tip the bow wave stands, as a share
 *  of the wave's own radius. */
const LUNGE_WAVE_AHEAD = 1 / 3;
/** lunge: how wide the bow wave opens, in radians each side of the bearing. */
const LUNGE_ARC_HALF = 0.9;
/** lunge: the round's alpha, which never fades. */
const LUNGE_ROUND_ALPHA = 0.95;

/** piston, card 15: how far the smear runs along the flight and how far it
 *  stands across it, as shares of the slab's own thickness and half width. */
const PISTON_SMEAR_LONG = 2, PISTON_SMEAR_ACROSS = 0.8;
/** piston: the white leading edge's alpha. */
const PISTON_EDGE_ALPHA = 0.9;

/** arc-discharge, card 16: the most steps the bolt may walk, which sizes the
 *  scratch. A row that asks for more is held here. */
const ARC_MAX_STEPS = 12;
/** arc-discharge: how far apart two rounds of one salvo flicker, so the two
 *  bolts never take the same shape in one frame. */
const ARC_SEED_STRIDE = 17;
/** arc-discharge: the round head's alpha. */
const ARC_HEAD_ALPHA = 0.95;

/** sweep, card 17: how much of the flight the aim ray shows for. */
const SWEEP_RAY_AT = 0.5;
/** sweep: the round head's alpha. */
const SWEEP_HEAD_ALPHA = 0.95;

/** The reused points of this file (R131). */
const P0: Pt = { x: 0, y: 0 };
const P1: Pt = { x: 0, y: 0 };
/** broadside's own muzzle, which stands astern of the event's source point,
 *  and the geometry of the flight that leaves it. */
const SRC: Pt = { x: 0, y: 0 };
const D0: Dir = { ux: 1, uy: 0, nx: 0, ny: 1, len: 1, ang: 0 };
/** arc-discharge's bolt, walked in place every frame. */
const ARC_X = new Float64Array(ARC_MAX_STEPS + 1);
const ARC_Y = new Float64Array(ARC_MAX_STEPS + 1);

/**
 * How far broadside's round stands off the straight line at `u`.
 *
 * The card's `f(u)`: the stand-off it left the beam at, run down to nothing
 * at the target and bowed out over the middle of the flight.
 */
function broadsideBend(off: number, u: number): number {
  return off * (1 - u) * (1 + BROADSIDE_BOW * Math.sin(Math.PI * u));
}

export const RECIPES: Readonly<Record<string, ShotRecipeFn>> = {
  /**
   * Card 10, POINT-BLANK FLASH (the AUTO look).
   *
   * The round crosses in a sixth of a second. The whole line lights at once
   * as a white snap inside a fat band of the faction ink, the muzzle blooms,
   * and a hard head runs down the line.
   */
  'point-blank-flash'(c) {
    const r = c.row;
    const f = 1 - c.p;
    const h = atOn(c.d, c.from, c.p, 0, P0);
    line(c, c.from.x, c.from.y, c.to.x, c.to.y, r.widthPx * f, c.ink, r.alpha * f);
    line(
      c, c.from.x, c.from.y, c.to.x, c.to.y,
      r.width2Px * f + FLASH_CORE_FLOOR, COMBAT_WHITE, r.alpha2 * f * f,
    );
    flare(
      c, c.from.x, c.from.y, r.lengthPx * f + FLASH_MUZZLE_FLOOR,
      COMBAT_WHITE, FLASH_MUZZLE_ALPHA * f,
    );
    disc(c, h.x, h.y, r.rPx, COMBAT_WHITE, FLASH_HEAD_ALPHA);
  },

  /**
   * Card 01, STITCH.
   *
   * Three short white dashes leave the bow in a row and stitch the gap. Each
   * round is a bright dash with a faint tail behind it, and the muzzle blooms
   * over the first fifth of the flight.
   */
  stitch(c) {
    const r = c.row, d = c.d;
    const h = atOn(d, c.from, c.p, 0, P0);
    const bx = h.x - d.ux * r.lengthPx, by = h.y - d.uy * r.lengthPx;
    line(c, h.x, h.y, bx, by, r.widthPx, COMBAT_WHITE, r.alpha);
    line(
      c, bx, by, h.x - d.ux * r.length2Px, h.y - d.uy * r.length2Px,
      r.width2Px, COMBAT_WHITE, r.alpha2,
    );
    if (c.p >= STITCH_MUZZLE_AT) return;
    flare(
      c, c.from.x, c.from.y, r.rPx, COMBAT_WHITE,
      STITCH_MUZZLE_ALPHA * (1 - c.p / STITCH_MUZZLE_AT),
    );
  },

  /**
   * Card 02, BURST FIRE.
   *
   * Ten small rounds at sixty milliseconds, each a short trail and a hard
   * head. A cross in the faction ink flashes on the muzzle as each one
   * leaves: a bar across the bearing and a stroke down it, both opening as
   * they fade.
   */
  'burst-fire'(c) {
    const r = c.row, d = c.d;
    const h = atOn(d, c.from, c.p, 0, P0);
    trail(c, h, r.lengthPx, r.widthPx, COMBAT_WHITE, r.alpha);
    disc(c, h.x, h.y, r.rPx, COMBAT_WHITE, BURST_HEAD_ALPHA);
    if (c.p >= BURST_MUZZLE_AT) return;
    const u = c.p / BURST_MUZZLE_AT;
    const a = r.alpha2 * (1 - u);
    const s = r.length2Px * (1 + BURST_CROSS_GROW * u);
    line(
      c, c.from.x - d.nx * s, c.from.y - d.ny * s,
      c.from.x + d.nx * s, c.from.y + d.ny * s, r.width2Px, c.ink, a,
    );
    line(
      c, c.from.x, c.from.y,
      c.from.x + d.ux * s * BURST_CROSS_LEAD,
      c.from.y + d.uy * s * BURST_CROSS_LEAD, r.width2Px, c.ink, a,
    );
  },

  /**
   * Card 04, BROADSIDE.
   *
   * Five rounds leave both beams of the shooter at once and bend in to the
   * target. The guns stand astern of the event's own source point, the odd
   * rounds take the starboard beam and the even ones the port beam, and a
   * flare on each beam marks the guns while the first rounds are in the
   * water.
   */
  broadside(c) {
    const r = c.row;
    SRC.x = c.from.x - c.d.ux * r.lengthPx;
    SRC.y = c.from.y - c.d.uy * r.lengthPx;
    const d = dirOf(SRC, c.to, D0);
    const off = (c.k % 2 ? 1 : -1)
      * (r.width2Px + r.width2Px * BROADSIDE_SPREAD * hash(c.k, 21));
    const h = atOn(d, SRC, c.p, broadsideBend(off, c.p), P0);
    const u1 = Math.min(1, c.p + BROADSIDE_LOOKAHEAD);
    const q = atOn(d, SRC, u1, broadsideBend(off, u1), P1);
    // The round's own heading is the bend's tangent, so the trail lies along
    // the curve and not along the straight line to the target.
    const ang = Math.atan2(q.y - h.y, q.x - h.x);
    line(
      c, h.x - Math.cos(ang) * r.length2Px, h.y - Math.sin(ang) * r.length2Px,
      h.x, h.y, r.widthPx, c.ink, r.alpha,
    );
    disc(c, h.x, h.y, r.rPx, COMBAT_WHITE, BROADSIDE_HEAD_ALPHA);
    if (c.p >= BROADSIDE_MUZZLE_AT || c.k >= r.count) return;
    flare(
      c, SRC.x + d.nx * off, SRC.y + d.ny * off, r.r2Px, c.ink,
      r.alpha2 * (1 - c.p / BROADSIDE_MUZZLE_AT),
    );
  },

  /**
   * Card 05, SPREAD CONE.
   *
   * Seven pale rounds leave at once and fan out to the spread the row states.
   * No two grains are the same size: the round's own index picks it, so the
   * cone reads as shot and not as a pattern.
   */
  'spread-cone'(c) {
    const r = c.row, d = c.d;
    const h = atOn(d, c.from, c.p, 0, P0);
    const s = CONE_SIZE_MIN + CONE_SIZE_SPAN * hash(c.k, 1);
    line(
      c, h.x, h.y, h.x - d.ux * r.lengthPx * s, h.y - d.uy * r.lengthPx * s,
      r.widthPx, PALE_INK, r.alpha,
    );
    disc(c, h.x, h.y, r.rPx * s, PALE_INK, r.alpha2);
  },

  /**
   * Card 09, SPEAR AND CASE.
   *
   * A long thin dart in the faction ink with a white tip, and a spent case
   * that flips astern of the muzzle, turns as it goes and sinks out of the
   * picture. Two per salvo: the gun that shows its own work.
   */
  'spear-and-case'(c) {
    const r = c.row, d = c.d;
    const h = atOn(d, c.from, c.p, 0, P0);
    dart(c, h.x, h.y, d.ang, r.lengthPx, r.widthPx, c.ink, r.alpha);
    disc(c, h.x, h.y, r.rPx, COMBAT_WHITE, CASE_TIP_ALPHA);
    const u = Math.min(1, c.p * CASE_RUSH);
    const back = r.r2Px + r.length2Px * u;
    const side = r.r2Px * CASE_SIDE;
    const cx = c.from.x - d.ux * back + d.nx * side;
    // The fall is the one motion that is not in the shooter's frame: a case
    // sinks down the screen whichever way the bow points, as the card draws.
    const cy = c.from.y - d.uy * back + d.ny * side + r.length2Px * u * u;
    rect(
      c, cx, cy, r.width2Px * 2, r.width2Px * 2 * CASE_THIN,
      u * r.speed + c.k, PALE_INK, r.alpha2 * (1 - u),
    );
  },

  /**
   * Card 12, LUNGE.
   *
   * A ram that sends a round. A ghost bow in the faction ink slides out of
   * the shooter and back over the first half of the flight, a pale bow wave
   * stands on its tip, and the round leaves from there.
   */
  lunge(c) {
    const r = c.row, d = c.d;
    const h = atOn(d, c.from, c.p, 0, P0);
    const u = Math.sin(Math.PI * Math.min(1, c.p / LUNGE_HALF));
    const reach = r.lengthPx * u;
    dart(
      c, c.from.x + d.ux * reach, c.from.y + d.uy * reach, d.ang,
      r.length2Px, r.widthPx, c.ink, r.alpha * (LUNGE_REST + u),
    );
    const w = reach + r.r2Px * LUNGE_WAVE_AHEAD;
    arcOf(
      c, c.from.x + d.ux * w, c.from.y + d.uy * w, r.r2Px, r.width2Px,
      d.ang - LUNGE_ARC_HALF, LUNGE_ARC_HALF * 2, PALE_INK, r.alpha2 * u,
    );
    disc(c, h.x, h.y, r.rPx, c.ink, LUNGE_ROUND_ALPHA);
    disc(c, h.x, h.y, r.width2Px, COMBAT_WHITE, LUNGE_ROUND_ALPHA);
  },

  /**
   * Card 15, PISTON.
   *
   * The round is a slab of pressure square to the flight: thick and narrow at
   * the muzzle, thin and wide at the target, with a white leading edge and a
   * smear of the faction ink behind it.
   */
  piston(c) {
    const r = c.row, d = c.d;
    const h = atOn(d, c.from, c.p, 0, P0);
    const hw = r.widthPx + r.lengthPx * c.p;
    const th = r.length2Px - r.rPx * c.p;
    const back = th + r.r2Px;
    blob(
      c, h.x - d.ux * back, h.y - d.uy * back,
      th * PISTON_SMEAR_LONG, hw * PISTON_SMEAR_ACROSS, d.ang, c.ink, r.alpha2,
    );
    // The card fills the slab ASTERN of the head, so its box centre stands
    // half a thickness back down the bearing. The white edge is the same
    // rectangle, one stroke wide, on the head itself.
    rect(
      c, h.x - d.ux * (th / 2), h.y - d.uy * (th / 2),
      th, hw * 2, d.ang, c.ink, r.alpha,
    );
    rect(
      c, h.x - d.ux * (r.width2Px / 2), h.y - d.uy * (r.width2Px / 2),
      r.width2Px, hw * 2, d.ang, COMBAT_WHITE, PISTON_EDGE_ALPHA,
    );
  },

  /**
   * Card 16, ARC DISCHARGE.
   *
   * A jagged electric bolt joins the muzzle to the round and flickers thirty
   * times a second. The bolt is walked in steps that wander widest in the
   * middle and meet the round exactly, and it is stroked twice: a fat band of
   * the faction ink and a thin white core over it.
   */
  'arc-discharge'(c) {
    const r = c.row, d = c.d;
    const n = Math.min(ARC_MAX_STEPS, Math.max(1, r.count));
    // The flicker is a step clock, not a smooth one: the whole bolt takes a
    // new shape `speed` times a second, and each round of the salvo its own.
    const fl = Math.floor(c.t * r.speed) + c.k * ARC_SEED_STRIDE;
    ARC_X[0] = c.from.x; ARC_Y[0] = c.from.y;
    for (let i = 1; i <= n; i++) {
      const off = i < n
        ? (hash(fl, i) - 0.5) * r.lengthPx * Math.sin((Math.PI * i) / n)
        : 0;
      const q = atOn(d, c.from, (c.p * i) / n, off, P0);
      ARC_X[i] = q.x; ARC_Y[i] = q.y;
    }
    polyline(c, ARC_X, ARC_Y, n + 1, r.widthPx, c.ink, r.alpha);
    polyline(c, ARC_X, ARC_Y, n + 1, r.width2Px, COMBAT_WHITE, r.alpha2);
    const h = atOn(d, c.from, c.p, 0, P0);
    disc(c, h.x, h.y, r.rPx, COMBAT_WHITE, ARC_HEAD_ALPHA);
  },

  /**
   * Card 17, SWEEP.
   *
   * Five rounds at seventy milliseconds, each a short white trail and a hard
   * head. The aim ray in the faction ink flicks to the target point of the
   * round that just left and fades over the first half of its flight, so the
   * ray sweeps across the target with the burst.
   */
  sweep(c) {
    const r = c.row, d = c.d;
    const h = atOn(d, c.from, c.p, 0, P0);
    if (c.p < SWEEP_RAY_AT) {
      line(
        c, c.from.x, c.from.y, c.to.x, c.to.y, r.widthPx, c.ink,
        r.alpha * (1 - c.p / SWEEP_RAY_AT),
      );
    }
    trail(c, h, r.lengthPx, r.width2Px, COMBAT_WHITE, r.alpha2);
    disc(c, h.x, h.y, r.rPx, COMBAT_WHITE, SWEEP_HEAD_ALPHA);
  },
};

/**
 * The cards of round 048 that wait for their own recipe, in card order.
 *
 * The table is EMPTY: every one of the ten looks the owner picked from
 * `archive/mockups/048-shots-melee.html` has its recipe above and its figure
 * row in `data/presentation/combat.toml`.
 */
export const PENDING: readonly string[] = [];
